import { useState } from "react";
import { Section } from "@/components/ds/Section/Section";
import { Container } from "@/components/ds/Container/Container";
import { Stack } from "@/components/ds/Stack/Stack";
import { Row } from "@/components/ds/Row/Row";
import { Heading } from "@/components/ds/Heading/Heading";
import { Text } from "@/components/ds/Text/Text";
import { Button } from "@/components/ds/Button/Button";
import { Reveal } from "@/components/ds/Reveal/Reveal";
import { CheckIcon } from "@/icons/CheckIcon";
import { BASE_PRICE_GBP, PER_CLEANER_GBP } from "@/domain/billing/pricing";
import styles from "./Pricing.module.css";

const BASE_PRICE = BASE_PRICE_GBP;
const PER_CLEANER = PER_CLEANER_GBP;
const DEFAULT_CLEANERS = 8;
const MIN_CLEANERS = 1;
const MAX_CLEANERS = 50;

const FEATURES = [
  "Unlimited cleaning sites",
  "GPS-verified check-in and check-out",
  "Live attendance dashboard, refreshes every 30 seconds",
  "Full attendance history, filterable by date, site, or cleaner",
  "Drag-and-drop weekly rotas with clash warnings",
  "Email support",
] as const;

function clampCleaners(value: number): number {
  if (Number.isNaN(value)) return DEFAULT_CLEANERS;
  return Math.max(MIN_CLEANERS, Math.min(999, Math.round(value)));
}

function formatPrice(amount: number): { pounds: string; pence: string } {
  const rounded = Math.round(amount * 100) / 100;
  const [pounds, pence = "00"] = rounded.toFixed(2).split(".");
  return { pounds, pence };
}

export function Pricing() {
  const [cleaners, setCleaners] = useState(DEFAULT_CLEANERS);
  const [inputValue, setInputValue] = useState(String(DEFAULT_CLEANERS));
  const total = BASE_PRICE + cleaners * PER_CLEANER;
  const { pounds, pence } = formatPrice(total);
  const fillPercent =
    ((cleaners - MIN_CLEANERS) / (MAX_CLEANERS - MIN_CLEANERS)) * 100;

  function updateCleaners(value: number) {
    const clamped = clampCleaners(value);
    setCleaners(clamped);
    setInputValue(String(clamped));
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    setInputValue(raw);
    const parsed = Number(raw);
    if (!Number.isNaN(parsed) && parsed >= MIN_CLEANERS) {
      setCleaners(Math.min(999, Math.round(parsed)));
    }
  }

  function handleInputBlur() {
    updateCleaners(Number(inputValue));
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      updateCleaners(Number(inputValue));
      (e.target as HTMLInputElement).blur();
    }
  }

  return (
    <Section background="white" id="pricing">
      <Container>
        <Reveal>
          <Stack gap={10} align="center">
            <Heading level={2} size="h2">
              Simple pricing, for cleaning companies of all sizes.
            </Heading>

            <div className={styles.layout}>
              {/* ── Left: hero pricing card ── */}
              <div className={styles.heroCard}>
                <div className={styles.heroInner}>
                  <Text as="span" size="xs" weight="semibold" className={styles.heroAnchor}>
                    LESS THAN ONE MISSED SHIFT A MONTH
                  </Text>

                  <div className={styles.priceFormula}>
                    <div className={styles.priceBlock}>
                      <span className={styles.heroCurrency}>£</span>
                      <span className={styles.heroAmount}>{BASE_PRICE}</span>
                      <span className={styles.heroUnit}>/mo</span>
                    </div>
                    <span className={styles.heroPlus}>+</span>
                    <div className={styles.priceBlock}>
                      <span className={styles.heroCurrency}>£</span>
                      <span className={styles.heroAmountSmall}>
                        {PER_CLEANER.toFixed(2)}
                      </span>
                      <span className={styles.heroUnit}>/cleaner/mo</span>
                    </div>
                  </div>

                  <Text size="sm" color="primary" className={styles.heroSub}>
                    One plan, every feature. No per-site charges. No tiers.
                    Add cleaners as you grow, remove them when the season
                    ends. You pay for who&apos;s on your rota, nothing else.
                  </Text>

                  <ul className={styles.features}>
                    {FEATURES.map((feature) => (
                      <li key={feature} className={styles.feature}>
                        <CheckIcon
                          width={16}
                          height={16}
                          className={styles.checkIcon}
                        />
                        <Text as="span" size="sm" color="primary">
                          {feature}
                        </Text>
                      </li>
                    ))}
                  </ul>

                  <Stack gap={3}>
                    <Button
                      href="#get-started"
                      variant="accent"
                      size="lg"
                      fullWidth
                    >
                      Start your free trial
                    </Button>
                    <Row gap={2} justify="center">
                      <Text as="span" size="xs" color="muted">
                        14 days free
                      </Text>
                      <Text as="span" size="xs" color="muted">
                        ·
                      </Text>
                      <Text as="span" size="xs" color="muted">
                        No credit card
                      </Text>
                      <Text as="span" size="xs" color="muted">
                        ·
                      </Text>
                      <Text as="span" size="xs" color="muted">
                        Cancel anytime
                      </Text>
                    </Row>
                  </Stack>
                </div>
              </div>

              {/* ── Right: dark calculator ── */}
              <div className={styles.calcSide}>
                <div className={styles.calcCard}>
                  <div className={styles.calcLabel}>Price calculator</div>

                  <div className={styles.calcTotal}>
                    <span className={styles.calcCurrency}>£</span>
                    <span className={styles.calcPounds}>{pounds}</span>
                    <span className={styles.calcPence}>.{pence}</span>
                    <span className={styles.calcPeriod}>/mo</span>
                  </div>

                  <div className={styles.calcBreakdown}>
                    £{BASE_PRICE} + £{PER_CLEANER.toFixed(2)} x {cleaners}{" "}
                    {cleaners === 1 ? "cleaner" : "cleaners"}
                  </div>

                  <div className={styles.calcSlider}>
                    <div className={styles.calcSliderHeader}>
                      <span className={styles.calcSliderLabel}>Team size</span>
                      <input
                        type="number"
                        min={MIN_CLEANERS}
                        max={MAX_CLEANERS}
                        value={inputValue}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        onKeyDown={handleInputKeyDown}
                        className={styles.calcInput}
                        aria-label="Number of cleaners"
                      />
                    </div>

                    <div className={styles.trackWrap}>
                      <div className={styles.track}>
                        <div
                          className={styles.trackFill}
                          style={{ width: `${fillPercent}%` }}
                        />
                      </div>
                      <input
                        type="range"
                        min={MIN_CLEANERS}
                        max={MAX_CLEANERS}
                        value={cleaners}
                        onChange={(e) => updateCleaners(Number(e.target.value))}
                        className={styles.rangeInput}
                        aria-label="Number of cleaners"
                      />
                    </div>

                    <div className={styles.trackLabels}>
                      <span>{MIN_CLEANERS}</span>
                      <span>{MAX_CLEANERS}</span>
                    </div>
                  </div>

                  <Button
                    href="#get-started"
                    variant="accent"
                    size="md"
                    fullWidth
                  >
                    Get started free
                  </Button>
                </div>
              </div>
            </div>
          </Stack>
        </Reveal>
      </Container>
    </Section>
  );
}
